import { apiClient } from './client';

export const FEATURE_KEYS = [
    'reports.create',
    'reports.export',
    'ai.credits',
    'surveys.send',
    'surveys.custom.create',
    'tests.send',
    'tests.correctWithAi',
    'patientLinks.send',
    'patients.active',
    'anamnesis.process',
    'reports.logo',
    'reports.letterhead',
    'reports.cover',
    'reports.customSections',
    'reports.customization',
    'platformTemplates.use',
    'reportTemplates.custom',
    'sectionDefaults.save',
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

export type EntitlementPlan = 'TRIAL' | 'BASIC' | 'INTERMEDIATE' | 'PRO';
export type PaidEntitlementPlan = Exclude<EntitlementPlan, 'TRIAL'>;

export type EntitlementStatus =
    | 'trial'
    | 'trial_not_started'
    | 'trial_expired'
    | 'active'
    | 'trialing'
    | 'canceled'
    | 'past_due'
    | 'incomplete'
    | 'incomplete_expired'
    | 'unpaid';

type EntitlementPeriod = 'trial' | 'billing_period';

export type EntitlementRule =
    | { kind: 'boolean'; enabled: boolean }
    | { kind: 'quota'; limit: number | null; period: EntitlementPeriod }
    | { kind: 'cap'; limit: number | null }
    | { kind: 'mode'; mode: string };

export type EntitlementDenialReason =
    | 'trial_not_started'
    | 'trial_forfeited'
    | 'trial_expired'
    | 'subscription_inactive'
    | 'feature_not_in_plan'
    | 'quota_exceeded'
    | 'resource_cap_exceeded'
    | 'mode_not_allowed';

export type EntitlementDecision = {
    allowed: boolean;
    feature: FeatureKey;
    plan: EntitlementPlan;
    rule: EntitlementRule;
    reason?: EntitlementDenialReason;
    limit?: number | null;
    used?: number;
    remaining?: number | null;
    renews_at?: string | null;
    mode?: string;
    available_from_plan?: {
        id: PaidEntitlementPlan;
        name: string;
    } | null;
};

export type Entitlements = {
    plan: EntitlementPlan;
    name: string;
    status: EntitlementStatus;
    active: boolean;
    period_start: string | null;
    period_end: string | null;
    features: Partial<Record<FeatureKey, EntitlementDecision>>;
};

type EntitlementCurrentPlan = {
    kind: 'trial' | 'paid';
    id?: PaidEntitlementPlan | null;
    name: string;
    active: boolean;
    status: EntitlementStatus;
    period_start: string | null;
    period_end: string | null;
    price_id: string | null;
    price: number | null;
    currency: string | null;
};

type EntitlementsEnvelope = {
    data: {
        current_plan: EntitlementCurrentPlan;
        features: Partial<Record<FeatureKey, Omit<EntitlementDecision, 'plan'>>>;
    };
};

const resolvePlanKey = (currentPlan: EntitlementCurrentPlan): EntitlementPlan => {
    if (currentPlan.kind === 'trial') return 'TRIAL';
    if (currentPlan.id) return currentPlan.id;

    const planName = currentPlan.name.trim().toLowerCase();
    if (planName.includes('intermediate')) return 'INTERMEDIATE';
    if (planName.includes('basic')) return 'BASIC';
    if (planName.includes('pro')) return 'PRO';

    return 'TRIAL';
};

const normalizeEntitlements = (payload: EntitlementsEnvelope['data']): Entitlements => {
    const plan = resolvePlanKey(payload.current_plan);
    const features = Object.fromEntries(
        Object.entries(payload.features).map(([feature, decision]) => [
            feature,
            decision ? { ...decision, plan } : decision,
        ]),
    ) as Partial<Record<FeatureKey, EntitlementDecision>>;

    return {
        plan,
        name: payload.current_plan.name,
        status: payload.current_plan.status,
        active: payload.current_plan.active,
        period_start: payload.current_plan.period_start,
        period_end: payload.current_plan.period_end,
        features,
    };
};

export const entitlementsApi = {
    getEntitlements: async (): Promise<Entitlements> => {
        const response = await apiClient.get<EntitlementsEnvelope>('/me/entitlements');

        return normalizeEntitlements((response as unknown as EntitlementsEnvelope).data);
    },
    optInTrial: async (): Promise<Entitlements> => {
        const response = await apiClient.post<EntitlementsEnvelope>('/me/trial/opt-in');

        return normalizeEntitlements((response as unknown as EntitlementsEnvelope).data);
    },
};
